import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ledgerService } from '../services';
import { useToast } from '../hook/use-toast';

const AdminLedger: React.FC = () => {
  const { toast } = useToast();
  const [provider, setProvider] = useState<'mercadopago' | 'pagarme' | 'all'>('all');
  const [selectedRunId, setSelectedRunId] = useState<string>('');

  const summaryQuery = useQuery({
    queryKey: ['ledger-summary'],
    queryFn: () => ledgerService.getSummary(),
  });
  const entriesQuery = useQuery({
    queryKey: ['ledger-entries'],
    queryFn: () => ledgerService.getEntries({ limit: 50, page: 1 }),
  });
  const runsQuery = useQuery({
    queryKey: ['ledger-recon-runs'],
    queryFn: () => ledgerService.getReconciliationRuns(),
  });
  const itemsQuery = useQuery({
    queryKey: ['ledger-recon-items', selectedRunId],
    queryFn: () => ledgerService.getReconciliationItems(selectedRunId),
    enabled: !!selectedRunId,
  });

  const runReconMutation = useMutation({
    mutationFn: () => ledgerService.runReconciliation({ provider }),
    onSuccess: () => toast({ title: 'Reconciliação iniciada/concluída com sucesso.' }),
    onError: (e: any) =>
      toast({ title: e?.response?.data?.message || e?.message || 'Erro ao reconciliar', variant: 'destructive' }),
  });

  const summary = summaryQuery.data?.data;
  const entries = entriesQuery.data?.data?.items || [];
  const runs = runsQuery.data?.data || [];
  const reconItems = itemsQuery.data?.data || [];
  const selectedRun = useMemo(() => runs.find((r) => r._id === selectedRunId), [runs, selectedRunId]);

  return (
    <Box>
      {summaryQuery.isLoading ? <CircularProgress size={24} /> : null}
      {summary ? (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Resumo
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            <Chip label={`Lançamentos: ${summary.totalEntries}`} />
            <Chip label={`Receita plataforma: R$ ${Number(summary.platformRevenue || 0).toFixed(2)}`} color="success" />
            <Chip label={`Reconciliações pendentes/falhas: ${summary.pendingRecon}`} color={summary.pendingRecon ? 'warning' : 'default'} />
          </Box>
        </Paper>
      ) : null}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <TextField
            select
            size="small"
            label="Provider"
            value={provider}
            onChange={(e) => setProvider(e.target.value as any)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="all">Todos</MenuItem>
            <MenuItem value="mercadopago">Mercado Pago</MenuItem>
            <MenuItem value="pagarme">Pagar.me</MenuItem>
          </TextField>
          <Button variant="contained" onClick={() => runReconMutation.mutate()} disabled={runReconMutation.isPending}>
            {runReconMutation.isPending ? 'Executando...' : 'Executar reconciliação'}
          </Button>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Lançamentos recentes
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Quando</TableCell>
              <TableCell>TxKey</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>PSP</TableCell>
              <TableCell>Valor</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>Sem lançamentos ainda.</TableCell>
              </TableRow>
            ) : (
              entries.map((entry) => {
                const total = (entry.lines || []).reduce((s, l) => s + Number(l.credit || 0), 0);
                return (
                  <TableRow key={entry._id}>
                    <TableCell>{new Date(entry.occurredAt).toLocaleString('pt-BR')}</TableCell>
                    <TableCell>{entry.txKey}</TableCell>
                    <TableCell>{entry.kind}</TableCell>
                    <TableCell>{entry.provider || '—'}</TableCell>
                    <TableCell>R$ {Number(total || 0).toFixed(2)}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Runs de reconciliação
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Provider</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Divergências</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>Ainda não há runs de reconciliação.</TableCell>
              </TableRow>
            ) : (
              runs.map((run) => {
                const diverg = Number(run.summary?.missingInternal || 0) + Number(run.summary?.missingProvider || 0);
                return (
                  <TableRow key={run._id}>
                    <TableCell>{new Date(run.createdAt).toLocaleString('pt-BR')}</TableCell>
                    <TableCell>{run.provider}</TableCell>
                    <TableCell>{run.status}</TableCell>
                    <TableCell>{diverg}</TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => setSelectedRunId(run._id)}>
                        Ver itens
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Paper>

      {selectedRunId ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Itens da reconciliação {selectedRun ? `(${new Date(selectedRun.createdAt).toLocaleString('pt-BR')})` : ''}
          </Typography>
          {itemsQuery.isLoading ? <CircularProgress size={22} /> : null}
          {itemsQuery.isError ? <Alert severity="error">Falha ao carregar itens da reconciliação.</Alert> : null}
          {!itemsQuery.isLoading ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>TxKey</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Detalhe</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reconItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>Sem divergências neste run.</TableCell>
                  </TableRow>
                ) : (
                  reconItems.map((item) => (
                    <TableRow key={item._id}>
                      <TableCell>{item.status}</TableCell>
                      <TableCell>{item.txKey || '—'}</TableCell>
                      <TableCell>{item.provider || '—'}</TableCell>
                      <TableCell>{item.details || '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          ) : null}
        </Paper>
      ) : null}
    </Box>
  );
};

export default AdminLedger;
